import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICryptoWallet } from '@common/api/crypto-wallet';
import
{
    GetWalletsFromExternal,
    ICreateWalletParams,
    IGetShadowInfo,
    IGetShadowInfoResponse,
    IKeyData,
    IPrivateData,
} from '@common/api/crypto-wallet-public';
import secrets from '@common/utils/secrets';
import { environment } from '@environment';
import { ConfigService, TConfig } from '@services/config/config.service';
import { CryptoService } from '@services/crypto/crypto.service';


@Injectable({
    providedIn: 'root'
})
export class SessionExternalService
{
    private config: TConfig;

    constructor(
        private configService: ConfigService,
        private cryptoService: CryptoService,
        private http: HttpClient,
    ) { }

    public async getWallets(_access_token: string): Promise<GetWalletsFromExternal>
    {
        return <GetWalletsFromExternal>(
            await this.http.post(
                '/api/crypto-wallet-public/get-user-wallets',
                { access_token: _access_token }
            ).toPromise()
        );
    }

    public async initKeys(access_token: string, serverKey: string): Promise<[ CryptoKey, Uint8Array ]>
    {
        this.config = await this.configService.get();

        const clientKey = await this.initUserKey(access_token);
        const key = await this.initServerKey(serverKey, clientKey);

        this.cryptoService.setExternalSecret(key);

        return key;
    }

    public async getShadowInfo(_access_token: string, key_id?: number): Promise<IGetShadowInfoResponse>
    {
        const body: IGetShadowInfo = { access_token: _access_token, key_id: key_id };

        return <IGetShadowInfoResponse>(
            await this.http.post(
                '/api/crypto-wallet-public/get-ott',
                body
            ).toPromise()
        );
    }

    public async addWallet(_access_token: string, _cryptowallet: ICryptoWallet, _wallet: any)
    {
        return await this.http.post(
            '/api/crypto-wallet-public/add-wallet',
            {
                access_token: _access_token,
                cryprowallet: _cryptowallet,
                wallet: _wallet
            }
        ).toPromise();
    }

    public async addCryptoWallet(_access_token: string, _cw_info: { key_id: number, backup_id: string, wallet_address: string; })
    {
        return await this.http.post(
            '/api/crypto-wallet-public/add-crypto-wallet',
            {
                access_token: _access_token,
                cw_info: _cw_info
            }
        ).toPromise();
    }

    public async createBackup(_privateData: IPrivateData, _key: any, _accessToken: string, sharedSecret: CryptoKey, iv: Uint8Array): Promise<string>
    {
        const encryptedPrivateData = await this.cryptoService.encrypt(_key.password, JSON.stringify(_privateData));
        const storedData = [
            this.cryptoService.hexEncode(encryptedPrivateData[ 0 ]),
            encryptedPrivateData[ 1 ]
        ];

        const dataEncrypted = JSON.stringify(storedData);

        const ottResponse = <IGetShadowInfoResponse>(
            await this.http.post(
                '/api/crypto-wallet-public/request-backup',
                {
                    key_id: _key.key_id,
                    access_token: _accessToken
                }
            ).toPromise()
        );

        const ott_decrypted = await this.cryptoService.decryptOtt(ottResponse.ott_hexed, sharedSecret, iv);

        let body = {
            challenge: ott_decrypted,
            backup: dataEncrypted
        };

        const response = await this.http.post(`${ environment.hyper_id.backup_api }store`, body).toPromise();

        return response[ 'id' ];
    }

    public async getBackup(backup_id: string, _key: IKeyData, _accessToken: string, sharedSecret: CryptoKey, iv: Uint8Array): Promise<IPrivateData>
    {
        const ottResponse = <IGetShadowInfoResponse>(
            await this.http.post(
                '/api/crypto-wallet-public/request-backup',
                {
                    key_id: _key.key_id,
                    access_token: _accessToken
                }
            ).toPromise()
        );

        const ott_decrypted = await this.cryptoService.decryptOtt(ottResponse.ott_hexed, sharedSecret, iv);

        let body =
        {
            backup_ids: [ backup_id ],
            challenge: ott_decrypted,
        };

        const response = await this.http.post(`${ environment.hyper_id.backup_api }load`, body).toPromise();

        const backupDataJSON = response[ 'backups' ][ 0 ].backup.data;
        const backupData = JSON.parse(backupDataJSON);

        const decryptedData = await this.cryptoService.decrypt(_key.password, this.cryptoService.hexDecode(backupData[ 0 ]), backupData[ 1 ]);

        if (!decryptedData[ 0 ] || !decryptedData[ 1 ])
        {
            return null;
        }

        return JSON.parse(decryptedData[ 0 ]);
    }

    public async getKey(sharedSecret: CryptoKey, iv: Uint8Array, createBody: ICreateWalletParams, key_id?: number): Promise<IKeyData>
    {
        const getShadowInfo: any = await this.getShadowInfo(createBody.access_token, key_id);

        const ott_decrypted = await this.cryptoService.decryptOtt(getShadowInfo.ott_hexed, sharedSecret, iv);

        const data = {
            request_id: 1,
            key_id: getShadowInfo.key.key_id,
            ott: ott_decrypted
        };

        const response: any = await this.http.post(
            `${ environment.hyper_id.resourceServer }/key/shadow-req/v2`,
            data,
            {
                headers: {
                    "Content-type": "application/json"  
                }
            }
        ).toPromise();

        const shadowTwo = response.shadow;

        const keyHex = secrets.combine([ atob(getShadowInfo.key.shadow), atob(shadowTwo) ]);
        const key = secrets.hex2str(keyHex);

        return {
            key_id: getShadowInfo.key.key_id,
            password: key
        };
    }

    private async setClientKey(clientKey, access_token)
    {
        const res = await this.http.post(
            '/api/crypto-wallet-public/set-public-key',
            {
                access_token: access_token,
                public_key: encodeURIComponent(clientKey)
            }
        ).toPromise();

        if (res[ 'result' ] !== 0)
        {
            throw new Error(`Failed to set public key response result: ${ res[ 'result' ] }`);
        }
    }

    private async initUserKey(access_token): Promise<string>
    {
        const keyNamedCurve = this.config.client_key.named_curve;

        try
        {
            const keyPair = this.cryptoService.generateEcKey2(keyNamedCurve);

            const pubKeyBase64: string = this.cryptoService.exportKeyToBase642(keyPair.publicKey);

            const privKeyBase64: string = this.cryptoService.exportKeyToBase642(keyPair.privateKey);

            await this.setClientKey(pubKeyBase64, access_token);

            return privKeyBase64;
        } catch (error)
        {
            throw error;
        }
    }

    private async initServerKey(serverPubKey: string, clientKeyBase64: string): Promise<[ CryptoKey, Uint8Array ]>
    {
        try
        {
            const serverKeyBase64 = decodeURIComponent(serverPubKey);

            const namedCurve = this.config.client_key.named_curve;
            const serverKey: Buffer = this.cryptoService.importKeyFromBase642(serverKeyBase64);

            const clientKey: Buffer = this.cryptoService.importKeyFromBase642(clientKeyBase64);

            const sharedBuf = this.cryptoService.deriveSecretKey2(clientKey, serverKey, namedCurve);

            if (sharedBuf)
            {
                const secretKey: CryptoKey = await this.cryptoService.getSecret(sharedBuf);

                const iv: Uint8Array = this.cryptoService.getIV(sharedBuf);

                return [ secretKey, iv ];
            }
            else
            {
                throw 'SharedBuf not found';
            }
        }
        catch (e)
        {
            console.error(e);
        }
    }
}
